import { baseApi, getProvidesIDTags } from "./baseApi";
import apiUtils from "../utils/api";

const BASE_URL = "quotes";

export const quoteApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getQuotes: builder.query({
      query: (data) => ({ url: BASE_URL, method: "get", data }),
      providesTags: (result) => getProvidesIDTags(result ? result.results : [], "Quote"),
      transformResponse: ({ results, ...rest }) => {
        const transformedData = results ? results.map((data) => apiUtils.transformQuoteData(data)) : [];
        return { results: transformedData, ...rest };
      },
    }),
    getQuoteById: builder.query({
      query: ({ id, ...data }) => ({ url: `${BASE_URL}/${id}`, method: "get", data }),
      providesTags: (result) => getProvidesIDTags([result], "Quote"),
      transformResponse: (response) => apiUtils.transformQuoteData(response),
    }),
    downloadQuoteById: builder.mutation({
      query: ({ data }) => ({ url: `${BASE_URL}/download`, method: "blob", data }),
    }),
    getQuoteActivityById: builder.query({
      query: ({ id, ...data }) => ({ url: `${BASE_URL}/${id}/history`, method: "get", data }),
      providesTags: (result) => getProvidesIDTags(result ? result.results : [], "Activity"),
    }),
    editQuote: builder.mutation({
      query: (data) => ({ url: `${BASE_URL}/${data.id}`, method: "put", data }),
      transformResponse: (response) => apiUtils.transformQuoteData(response),
      invalidatesTags: (result, error, arg) => [{ type: "Quote", id: arg.id }, "SalePriceRequestCount"],
    }),
    deleteQuote: builder.mutation({
      query: (data) => ({ url: `${BASE_URL}/${data.id}`, method: "delete" }),
      // invalidating quote will reload that id, causing errors to display
      // invalidatesTags: (result, error, arg) => [{ type: "Quote", id: arg.id }],
    }),
    verifyQuote: builder.mutation({
      query: (data) => ({ url: `${BASE_URL}/verify/${data.id}`, method: "put", data }),
      transformResponse: (response) => apiUtils.transformQuoteData(response),
      invalidatesTags: (result, error, arg) => [{ type: "Quote", id: arg.id }],
    }),
    unverifyQuote: builder.mutation({
      query: (data) => ({ url: `${BASE_URL}/unverify/${data.id}`, method: "put", data }),
      transformResponse: (response) => apiUtils.transformQuoteData(response),
      invalidatesTags: (result, error, arg) => [{ type: "Quote", id: arg.id }],
    }),
    duplicateQuote: builder.mutation({
      query: (data) => ({ url: `${BASE_URL}/duplicate/${data.id}`, method: "put", data }),
      transformResponse: (response) => apiUtils.transformQuoteData(response),
      providesTags: (result) => getProvidesIDTags([result], "Quote"),
    }),
    updateQuotePrices: builder.mutation({
      query: (data) => ({ url: `${BASE_URL}/update_prices/${data.id}`, method: "put", data }),
      transformResponse: (response) => apiUtils.transformQuoteData(response),
      invalidatesTags: (result, error, arg) => [{ type: "Quote", id: arg.id }],
    }),
    addQuote: builder.mutation({
      query: (data) => ({ url: BASE_URL, method: "post", data }),
      transformResponse: (response) => apiUtils.transformQuoteData(response),
      invalidatesTags: (result, error, arg) => [{ type: "Quote", id: arg.id }, "SalePriceRequestCount"],
    }),
    createSampleRecipe: builder.mutation({
      query: ({ id, ...data }) => ({ url: `${BASE_URL}/sample-recipe/${id}`, method: "post", data }),
      transformResponse: (response) => apiUtils.transformQuoteData(response),
      invalidatesTags: (result, error, arg) => [{ type: "Quote", id: arg.id }],
    }),
    updateSampleRecipe: builder.mutation({
      query: ({ id, ...data }) => ({ url: `${BASE_URL}/sample-recipe/${id}`, method: "put", data }),
      transformResponse: (response) => apiUtils.transformQuoteData(response),
      invalidatesTags: (result, error, arg) => [{ type: "Quote", id: arg.id }],
    }),
    getSalePriceRequests: builder.query({
      query: (data) => ({ url: `${BASE_URL}/price-requests`, method: "get", data }),
      providesTags: (result) => getProvidesIDTags(result ? result.results : [], "SalePriceRequest"),
      transformResponse: ({ results, ...rest }) => {
        const transformedData = results ? results.map((data) => apiUtils.transformQuoteData(data)) : [];
        return { results: transformedData, ...rest };
      },
    }),
    getSalePriceRequestCount: builder.query({
      query: (data) => ({ url: `${BASE_URL}/price-requests/count`, method: "get", data }),
      providesTags: ["SalePriceRequestCount"],
    }),
    updateSalePriceRequest: builder.mutation({
      query: (data) => ({ url: `${BASE_URL}/price-requests/${data.id}`, method: "put", data }),
      transformResponse: (response) => apiUtils.transformQuoteData(response),
      invalidatesTags: (result, error, arg) => [{ type: "SalePriceRequest", id: arg.id }, "SalePriceRequestCount"],
    }),
  }),
  overrideExisting: true,
});

// auto-generated based on the defined endpoints
export const {
  useGetQuotesQuery,
  useGetQuoteByIdQuery,
  useGetQuoteActivityByIdQuery,
  useAddQuoteMutation,
  useEditQuoteMutation,
  useDeleteQuoteMutation,
  useVerifyQuoteMutation,
  useUnverifyQuoteMutation,
  useDuplicateQuoteMutation,
  useCreateSampleRecipeMutation,
  useUpdateSampleRecipeMutation,
  useGetSalePriceRequestsQuery,
  useUpdateSalePriceRequestMutation,
  useUpdateQuotePricesMutation,
  useGetSalePriceRequestCountQuery,
  useDownloadQuoteByIdMutation,
} = quoteApi;
